import { AutoComplete as AntAutoComplete } from 'antd'
import { AutoCompleteProps as AntAutoCompleteProps } from 'antd/lib/auto-complete'
import { FC, ReactNode, useMemo } from 'react'

export interface AutoCompleteOption {
    value: any
    label: ReactNode
    key?: string
    [key: string]: any
}

interface AutoCompleteProps extends Omit<AntAutoCompleteProps, 'options' | 'value' | 'onChange'> {
    options?: AutoCompleteOption[]
    value?: any
    onChange?: (value: any) => void
    getOptionKey?: (option: AutoCompleteOption) => string | undefined
}

const AutoComplete: FC<AutoCompleteProps> = ({
    options = [],
    value,
    onChange,
    getOptionKey,
    ...restProps
}) => {
    const transformedOptions = useMemo(() => {
        return options.map(opt => ({
            ...opt,
            value: String(opt.value),
        }))
    }, [options])

    const displayValue = useMemo(() => {
        if (value === undefined || value === null) return undefined

        if (getOptionKey) {
            const optionByKey = options.find(opt => getOptionKey(opt) === value)
            if (optionByKey) {
                return String(optionByKey.value)
            }
        }

        return String(value)
    }, [value, options, getOptionKey])

    const handleChange = (newValue: string) => {
        if (!onChange) return

        const option = options.find(opt => String(opt.value) === newValue)

        if (option && getOptionKey) {
            const key = getOptionKey(option)
            if (key) {
                onChange(key)
                return
            }
        }

        onChange(newValue)
    }

    return (
        <AntAutoComplete
            {...restProps}
            options={transformedOptions}
            value={displayValue}
            onChange={handleChange}
        />
    )
}

export default AutoComplete
